import React, { useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronRight, X, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../apiConfig/api';

// Asset Details Modal Component
const AssetDetailsModal = ({ isOpen, onClose, assetDetails, isLoading, wardInfo, onAssetClick, status }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  if (!isOpen) return null;
  
  // Filter assets based on search query
  const filteredAssets = searchQuery.trim() === '' 
    ? assetDetails 
    : assetDetails.filter(asset => {
        const query = searchQuery.toLowerCase();
        return (
          (asset.street_name && asset.street_name.toLowerCase().includes(query)) ||
          (asset.owner && asset.owner.toLowerCase().includes(query)) ||
          (asset.eb_no && asset.eb_no.toLowerCase().includes(query))
        );
      });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-800">
            Asset Details - {wardInfo?.ward_name || 'N/A'} - {status === 'completed' ? 'Surveys Completed' : 'Survey Pending'}
          </h2>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        
        {/* Add search input below the header */}
        <div className="px-4 py-3 border-b border-gray-200 sticky top-16 bg-white">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search by street name, owner or EB number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        <div className="overflow-auto p-4">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : filteredAssets.length > 0 ? (
            <div className="grid gap-4">
              {filteredAssets.map((asset, index) => (
         <div 
  key={index} 
  onClick={() => onAssetClick(asset.id)}
  className="border border-gray-200 rounded-lg p-4 hover:shadow-lg hover:border-gray-300 transition-all cursor-pointer"
>
 <div className="grid grid-cols-3 gap-4">
  <div>
    <p className="text-sm text-gray-500">Street Name</p>
    <p className="font-medium">{asset.street_name || 'N/A'}</p>
  </div>
  <div>
    <p className="text-sm text-gray-500">Owner Name</p>
    <p className="font-medium">{asset.owner || 'N/A'}</p>
  </div>
  <div>
    <p className="text-sm text-gray-500">EB Number</p>
    <p className="font-medium">{asset.eb_no || 'N/A'}</p>
  </div>
  <div>
    <p className="text-sm text-gray-500">Status</p>
    <p className={`font-medium ${
      status === 'completed' ? 'text-green-600' : 'text-orange-600'
    }`}>
      {status === 'completed' ? 'Completed' : 'Pending'}
    </p>
  </div>
  <div>
    <p className="text-sm text-gray-500">Created Date</p>
    <p className="font-medium text-sm">
      {asset.created_at 
        ? new Date(asset.created_at).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit', 
            year: 'numeric'
          }) + ' ' + 
          new Date(asset.created_at).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })
        : 'N/A'
      }
    </p>
  </div>
  <div>
    <p className="text-sm text-gray-500">Usage</p>
    <p className="font-medium">{asset.usage || 'N/A'}</p>
  </div>
</div>
                  {status === 'completed' && (
                    <div className="mt-3 flex justify-end">
                      <button className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                        View Details <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {searchQuery.trim() !== '' 
                ? "No matching assets found" 
                : "No asset details available"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Sub-components for the asset details
const SortButton = ({ label, field, sortConfig, onSort }) => (
  <button
    onClick={() => onSort(field)}
    className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
  >
    {label}
    {sortConfig.key !== field ? (
      <ArrowUpDown className="h-4 w-4" />
    ) : sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    )}
  </button>
);

const CompletedSurveyHeader = () => (
  <div className="hidden lg:grid grid-cols-4 gap-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg px-4">
    <div className="col-span-1">
      <h4 className="text-sm font-medium text-gray-600">Ward Details</h4>
    </div>
    <div className="text-center">
      <h4 className="text-sm font-medium text-gray-600">Completed</h4>
    </div>
    <div className="text-center">
      <h4 className="text-sm font-medium text-gray-600">Pending</h4>
    </div>
    <div className="text-center">
      <h4 className="text-sm font-medium text-gray-600">Total</h4>
    </div>
  </div>
);

const CompletedSurveyItem = ({ report, onCompletedClick, onPendingClick }) => {
  const completed = parseInt(report.completed || 0);
  const pending = parseInt(report.pending || 0);
  const total = completed + pending;

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-4 gap-4 py-3 border-b border-gray-100 last:border-0 px-4 hover:bg-gray-50">
      <div className="col-span-1">
        <h3 className="font-medium text-gray-800">{report.ward_name}</h3>
        <div className="text-xs text-gray-500 mt-1">
          <p>{report.org_name}</p>
          <p>{report.zone_name}</p>
        </div>
      </div>
      <div className="flex justify-between lg:justify-center items-center">
        <span className="text-sm text-gray-500 lg:hidden">Completed:</span>
        {completed > 0 ? (
          <button
            onClick={() => onCompletedClick(report)}
            className="font-medium text-green-600 hover:text-green-700 transition-colors hover:underline"
          >
            {report.completed || '0'}
          </button>
        ) : (
          <span className="font-medium text-gray-400">0</span>
        )}
      </div>
      <div className="flex justify-between lg:justify-center items-center">
        <span className="text-sm text-gray-500 lg:hidden">Pending:</span>
        {pending > 0 ? (
          <button
            onClick={() => onPendingClick(report)}
            className="font-medium text-orange-600 hover:text-orange-700 transition-colors hover:underline"
          >
            {report.pending || '0'}
          </button>
        ) : (
          <span className="font-medium text-gray-400">0</span>
        )}
      </div>
      <div className="flex justify-between lg:justify-center items-center">
        <span className="text-sm text-gray-500 lg:hidden">Total:</span>
        <span className="font-medium text-gray-800">{total}</span>
      </div>
    </div>
  );
};

const LoadingItem = () => (
  <div className="grid grid-cols-4 gap-4 py-3">
    <div className="col-span-1 space-y-2">
      <div className="h-4 bg-gray-200 rounded w-32"></div>
      <div className="h-3 bg-gray-200 rounded w-24"></div>
    </div>
    <div className="flex justify-center">
      <div className="h-4 bg-gray-200 rounded w-12"></div>
    </div>
    <div className="flex justify-center">
      <div className="h-4 bg-gray-200 rounded w-12"></div>
    </div>
    <div className="flex justify-center">
      <div className="h-4 bg-gray-200 rounded w-12"></div>
    </div>
  </div>
);

const AssetDetailsSection = ({ isLoading, assetReports, onCompletedClick, onNavigateToReport }) => {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null
  });
  
  // Asset modal states
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [selectedWardInfo, setSelectedWardInfo] = useState(null);
  const [assetDetails, setAssetDetails] = useState([]);
  const [isLoadingAssetDetails, setIsLoadingAssetDetails] = useState(false);
  const [assetModalStatus, setAssetModalStatus] = useState('completed');

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleCompletedClick = async (report) => {
    setSelectedWardInfo(report);
    setIsAssetModalOpen(true);
    setIsLoadingAssetDetails(true);
    setAssetModalStatus('completed');

    try {
      // Get org_code from localStorage
      const orgCode = localStorage.getItem('organization_code');
      
      const response = await api.get('/asset/report', {
        params: {
          org: orgCode,
          ward: report.ward_name,
          status: 'completed'
        }
      });
      setAssetDetails(response.data.data || []);
    } catch (error) {
      console.error('Error fetching asset details:', error);
      setAssetDetails([]);
    } finally {
      setIsLoadingAssetDetails(false);
    }
  };

  const handlePendingClick = async (report) => {
    setSelectedWardInfo(report);
    setIsAssetModalOpen(true);
    setIsLoadingAssetDetails(true);
    setAssetModalStatus('active'); // Use 'active' for API, but show as 'pending' in UI

    try {
      // Get org_code from localStorage
      const orgCode = localStorage.getItem('organization_code');
      
      const response = await api.get('/asset/report', {
        params: {
          org: orgCode,
          ward: report.ward_name,
          status: 'active' // API expects 'active' for pending items
        }
      });
      setAssetDetails(response.data.data || []);
    } catch (error) {
      console.error('Error fetching asset details:', error);
      setAssetDetails([]);
    } finally {
      setIsLoadingAssetDetails(false);
    }
  };

 const handleAssetClick = (assetId) => {
  if (assetModalStatus === 'active') {
    // For pending/active items, navigate to filters
    const wardDetails = localStorage.getItem('ward_details');
    const wardData = wardDetails ? JSON.parse(wardDetails) : [];
    
    navigate('/filters', { 
      state: { 
        wardData,
        orgId: localStorage.getItem('org_id'),
        userId: localStorage.getItem('user_id'),
        organizationName: localStorage.getItem('organization_name'),
        zoneName: localStorage.getItem('zone_name'),
        orgCode: localStorage.getItem('organization_code')
      }
    });
  } else if (assetId) {
    // For completed items, navigate to report
    onNavigateToReport(assetId);
  }
};

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return assetReports;

    return [...assetReports].sort((a, b) => {
      if (sortConfig.key === 'total') {
        const aCompleted = parseInt(a.completed || 0);
        const aPending = parseInt(a.pending || 0);
        const aTotal = aCompleted + aPending;
        
        const bCompleted = parseInt(b.completed || 0);
        const bPending = parseInt(b.pending || 0);
        const bTotal = bCompleted + bPending;
        
        return sortConfig.direction === 'asc' ? aTotal - bTotal : bTotal - aTotal;
      }
      
      const aValue = parseInt(a[sortConfig.key]) || 0;
      const bValue = parseInt(b[sortConfig.key]) || 0;
      
      if (sortConfig.direction === 'asc') {
        return aValue - bValue;
      }
      return bValue - aValue;
    });
  }, [assetReports, sortConfig]);

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
          <h2 className="text-lg font-semibold text-gray-800">Asset Details</h2>
          <div className="flex gap-2 flex-wrap">
            <SortButton 
              label="Completed"
              field="completed"
              sortConfig={sortConfig}
              onSort={handleSort}
            />
            <SortButton 
              label="Pending"
              field="pending"
              sortConfig={sortConfig}
              onSort={handleSort}
            />
            <SortButton 
              label="Total"
              field="total"
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
        </div>
        <div className="rounded-lg border border-gray-200">
          <CompletedSurveyHeader />
          <div className="divide-y divide-gray-200">
            {isLoading ? (
              <div className="px-4">
                {[1, 2, 3].map((i) => (
                  <LoadingItem key={i} />
                ))}
              </div>
            ) : sortedData.length > 0 ? (
              sortedData.map((report, index) => (
                <CompletedSurveyItem
                  key={index}
                  report={report}
                  onCompletedClick={handleCompletedClick}
                  onPendingClick={handlePendingClick}
                />
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No surveys found</p>
            )}
          </div>
        </div>
      </div>

      {/* Asset Details Modal */}
      <AssetDetailsModal 
        isOpen={isAssetModalOpen}
        onClose={() => {
          setIsAssetModalOpen(false);
          setSelectedWardInfo(null);
          setAssetDetails([]);
        }}
        assetDetails={assetDetails}
        isLoading={isLoadingAssetDetails}
        wardInfo={selectedWardInfo}
        onAssetClick={handleAssetClick}
        status={assetModalStatus === 'active' ? 'pending' : assetModalStatus}
      />
    </div>
  );
};

export default AssetDetailsSection;